import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { connect, type LiveEvent } from './connection';

type Handler = (event: LiveEvent) => void;
type Live = { handlers: Set<Handler>; polling: boolean };

const LiveContext = createContext<Live | null>(null);

/**
 * The app session's one stream (UI architecture §5; D-062), shared by every screen below it.
 * `enabled` false (logged out) closes it; `pollingOnly` (the admin panel) opens none and polls the
 * queries with `meta: { poll: true }`.
 */
export function LiveProvider({
  url = '/api/stream',
  pollingOnly = false,
  enabled = true,
  children,
}: {
  url?: string;
  pollingOnly?: boolean;
  enabled?: boolean;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [handlers] = useState(() => new Set<Handler>());
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const disconnect = connect({
      url,
      pollingOnly,
      queryClient,
      onEvent: (event) => {
        for (const handler of handlers) handler(event);
      },
      onPolling: setPolling,
    });
    return () => {
      disconnect();
      setPolling(false);
    };
  }, [url, pollingOnly, enabled, queryClient, handlers]);

  const live = useMemo(() => ({ handlers, polling }), [handlers, polling]);
  return <LiveContext value={live}>{children}</LiveContext>;
}

/**
 * Subscribes a screen to the stream: `handler` hears each event after its cache action, `ready`
 * and `resync` included, never the heartbeat. `polling` is true while polling replaces the stream
 * (the lost-stream banner, merchant panel screens §4.3).
 */
export function useLiveEvents(handler?: Handler): { polling: boolean } {
  const live = useContext(LiveContext);
  if (live === null) throw new Error('useLiveEvents() needs a <LiveProvider> above it.');
  const onEvent = useEffectEvent((event: LiveEvent) => handler?.(event));
  const { handlers } = live;
  useEffect(() => {
    const listener = (event: LiveEvent) => onEvent(event);
    handlers.add(listener);
    return () => {
      handlers.delete(listener);
    };
  }, [handlers]);
  return { polling: live.polling };
}
